import React, { useState, cloneElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Download, Monitor, Mail, Bell, ArrowRight, Trophy, Users } from 'lucide-react';
export function DepartmentPage() {
  return <div className="bg-white min-h-screen pt-24 pb-20" data-id="element-856">
      {/* Hero Canvas */}
      <section className="relative h-[40vh] min-h-[300px] flex items-center justify-center overflow-hidden bg-navy text-white" data-id="element-857">
        {/* Cybernetic SVG Pattern Overlay */}
        <div className="absolute inset-0 z-0 opacity-[0.08]" style={{
        backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")"
      }} data-id="element-858" />

        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto" data-id="element-859">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-860">
            <div className="inline-flex items-center space-x-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 mb-6" data-id="element-861">
              <span className="text-xs font-bold tracking-wider uppercase text-white" data-id="element-862">
                Faculty of Sciences & Engineering
              </span>
            </div>
            <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight leading-tight mb-4 text-white" data-id="element-863">
              Department of <br data-id="element-864" />
              <span className="text-white/85" data-id="element-865">
                Computer Science & Engineering
              </span>
            </h1>
          </motion.div>
        </div>
      </section>

      {/* 8/4 Grid Layout */}
      <section className="max-w-7xl mx-auto px-6 mt-16" data-id="element-866">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12" data-id="element-867">
          {/* LEFT: Main Column (8 cols) */}
          <div className="lg:col-span-8 space-y-12" data-id="element-868">
            {/* Program Tracks Accordion */}
            <div data-id="element-869">
              <h2 className="text-2xl font-bold mb-6 flex items-center text-navy" data-id="element-870">
                <Monitor className="w-6 h-6 mr-3 text-teal" data-id="element-871" />
                Program Tracks
              </h2>
              <div className="space-y-4" data-id="element-872">
                <AccordionItem title="Undergraduate Programs" programs={['B.Sc. in Computer Science & Engineering', 'B.Sc. in Data Science']} defaultOpen={true} data-id="element-873" />
                <AccordionItem title="Graduate Programs" programs={['M.Sc. in Cyber Security', 'M.Sc. in Artificial Intelligence & ML']} data-id="element-874" />
              </div>
            </div>

            {/* Department Spotlight Slider */}
            <div data-id="element-875">
              <h2 className="text-2xl font-bold mb-6 flex items-center text-navy" data-id="element-876">
                <Trophy className="w-6 h-6 mr-3 text-gold" data-id="element-877" />
                Department Spotlight
              </h2>
              <div className="bg-white border border-slate-200 shadow-md rounded-2xl overflow-hidden relative group" data-id="element-878">
                <img src="https://images.unsplash.com/photo-1504384308090-c894fdcc538d?q=80&w=2070&auto=format&fit=crop" alt="Hackathon" className="w-full h-[300px] object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-879" />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/90 via-navy/40 to-transparent" data-id="element-880" />
                <div className="absolute bottom-0 left-0 right-0 p-8" data-id="element-881">
                  <div className="bg-gold/20 text-gold text-xs font-bold px-3 py-1 rounded-full w-fit mb-3 border border-gold/30" data-id="element-882">
                    Achievement
                  </div>
                  <h3 className="text-2xl font-bold mb-2 text-white" data-id="element-883">
                    EWU CSE Team Wins National Hackathon 2025
                  </h3>
                  <p className="text-white/80 text-sm" data-id="element-884">
                    Our undergraduate team secured 1st place among 50+
                    universities with their innovative AI-driven healthcare
                    solution.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT: Sidebar Column (4 cols) */}
          <div className="lg:col-span-4 space-y-8" data-id="element-885">
            {/* Quick Action Deck */}
            <div className="space-y-3" data-id="element-886">
              <ActionTile icon={<Download data-id="element-888" />} title="Download Syllabus PDF" desc="Latest curriculum for 2026" data-id="element-887" />
              <ActionTile icon={<Monitor data-id="element-890" />} title="View Lab Facilities" desc="Explore our 12 specialized labs" data-id="element-889" />
              <ActionTile icon={<Mail data-id="element-892" />} title="Contact Dept. Admin" desc="Advising & general queries" data-id="element-891" />
            </div>

            {/* Active Announcements */}
            <div className="bg-white border border-slate-200 shadow-md rounded-2xl p-6" data-id="element-893">
              <h3 className="text-lg font-bold mb-6 flex items-center border-b border-slate-200 pb-4 text-navy" data-id="element-894">
                <Bell className="w-5 h-5 mr-2 text-gold" data-id="element-895" />
                CSE Alerts
              </h3>
              <div className="space-y-5" data-id="element-896">
                <Announcement date="May 24, 2026" title="Advising for Summer 2026 starts" urgent data-id="element-897" />
                <Announcement date="May 15, 2026" title="Registration deadline for AI Symposium" data-id="element-898" />
                <Announcement date="May 10, 2026" title="New elective course: Quantum Computing basics" data-id="element-899" />
              </div>
              <button className="w-full mt-6 py-2 text-sm text-center text-slate-500 hover:text-navy transition-colors" data-id="element-900">
                View All Alerts
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>;
}
function AccordionItem({
  title,
  programs,
  defaultOpen = false
}: {
  title: string;
  programs: string[];
  defaultOpen?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return <div className="bg-white border border-slate-200 shadow-sm rounded-2xl overflow-hidden" data-id="element-901">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full px-6 py-5 flex items-center justify-between hover:bg-slate-50 transition-colors" data-id="element-902">
        <span className="text-lg font-bold text-navy" data-id="element-903">{title}</span>
        <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} data-id="element-904" />
      </button>

      <AnimatePresence data-id="element-905">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3
      }} className="border-t border-slate-200" data-id="element-906">
            <div className="p-6 bg-slate-50" data-id="element-907">
              <div className="space-y-6" data-id="element-908">
                {programs.map((prog, idx) => <div key={idx} className="space-y-4" data-id="element-909">
                    <h4 className="font-semibold text-teal flex items-center" data-id="element-910">
                      <ArrowRight className="w-4 h-4 mr-2" data-id="element-911" /> {prog}
                    </h4>

                    {/* Clean Grid Curriculum Breakdown */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-6" data-id="element-912">
                      <div className="bg-white rounded-xl p-4 border border-slate-200" data-id="element-913">
                        <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3" data-id="element-914">
                          Core Foundation
                        </div>
                        <div className="flex flex-wrap gap-2" data-id="element-915">
                          <CourseChip code="CSE101" name="Programming" data-id="element-916" />
                          <CourseChip code="CSE205" name="Data Structures" data-id="element-917" />
                          <CourseChip code="CSE207" name="Algorithms" data-id="element-918" />
                        </div>
                      </div>
                      <div className="bg-white rounded-xl p-4 border border-slate-200" data-id="element-919">
                        <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3" data-id="element-920">
                          Advanced Tracks
                        </div>
                        <div className="flex flex-wrap gap-2" data-id="element-921">
                          <CourseChip code="CSE411" name="Machine Learning" data-id="element-922" />
                          <CourseChip code="CSE445" name="Cloud Computing" data-id="element-923" />
                        </div>
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
function CourseChip({
  code,
  name
}: {
  code: string;
  name: string;
}) {
  return <div className="inline-flex items-center px-2.5 py-1 rounded-md bg-slate-100 border border-slate-200 text-xs hover:bg-slate-200 transition-colors cursor-pointer group" data-id="element-924">
      <span className="font-bold text-navy mr-1.5" data-id="element-925">{code}</span>
      <span className="text-slate-600" data-id="element-926">{name}</span>
    </div>;
}
function ActionTile({
  icon,
  title,
  desc
}: {
  icon: React.ReactNode;
  title: string;
  desc: string;
}) {
  return <button className="w-full bg-white border border-slate-200 shadow-sm hover:shadow-md p-5 rounded-xl flex items-center text-left group transition-all duration-300" data-id="element-927">
      <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center mr-4 group-hover:bg-navy group-hover:text-white transition-colors" data-id="element-928">
        {cloneElement(icon as React.ReactElement, {
        className: 'w-6 h-6 text-slate-500 group-hover:text-white'
      })}
      </div>
      <div data-id="element-929">
        <div className="font-bold text-navy mb-0.5" data-id="element-930">{title}</div>
        <div className="text-xs text-slate-500" data-id="element-931">{desc}</div>
      </div>
    </button>;
}
function Announcement({
  date,
  title,
  urgent = false
}: {
  date: string;
  title: string;
  urgent?: boolean;
}) {
  return <div className="relative pl-4 border-l-2 border-slate-200 hover:border-teal transition-colors group cursor-pointer" data-id="element-932">
      <div className={`absolute -left-[5px] top-1.5 w-2 h-2 rounded-full ${urgent ? 'bg-gold animate-pulse' : 'bg-slate-300 group-hover:bg-teal'}`} data-id="element-933" />
      <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1" data-id="element-934">
        {date}
      </div>
      <div className="text-sm font-medium text-slate-800 group-hover:text-navy leading-snug" data-id="element-935">
        {title}
      </div>
    </div>;
}